import type { AuditEvent, AuditEventType } from '../api/auditApi';

export const EVENT_LABELS: Record<AuditEventType, string> = {
  login: 'Sign-in',
  login_failed: 'Failed sign-in',
  logout: 'Sign-out',
  page_view: 'Page view',
  impersonation: 'Viewed as practice',
  feature_flag_change: 'Feature flag changed',
  comms_preference_change: 'Communication preference changed',
  skill_change_proposed: 'Skill change proposed',
  skill_override_change: 'Practice skill override changed',
  notebook_change: 'Client facts removed',
  texting_setup_change: 'Texting set up by PracticeNova',
  tracked_link_change: 'Tracked link changed',
};

/** "Today 14:31:55" split into a time and a day line. */
export function formatEventTime(iso: string): { time: string; day: string } {
  const date = new Date(iso);
  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

  let day: string;
  if (sameDay(date, today)) day = 'Today';
  else if (sameDay(date, yesterday)) day = 'Yesterday';
  else day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return { time, day };
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * What goes in the "Page / detail" column. Page views show the route; auth
 * events show their reason ("Sign-in link expired") instead.
 */
export function eventDetail(event: AuditEvent): { primary: string; secondary: string | null } {
  if (event.eventType === 'page_view') {
    return { primary: event.label ?? event.path ?? '—', secondary: event.path };
  }
  return { primary: event.label ?? EVENT_LABELS[event.eventType], secondary: event.path };
}

/** "Chrome · macOS" from a user-agent string — enough to recognise a device. */
export function describeUserAgent(ua: string | null): string | null {
  if (!ua) return null;

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : null;

  const os = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;

  const parts = [browser, os].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
